import React from 'react';
import { X, ExternalLink, TrendingUp, TrendingDown } from 'lucide-react';

function formatPrice(val) {
  if (val == null) return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PredictionPopover({ prediction, loading, onClose }) {
  return (
    <div className="absolute bottom-4 left-4 z-50 w-80 max-h-[80vh] overflow-y-auto rounded-xl border border-[#2a2e39] bg-[#1e222d] shadow-2xl flex flex-col">
      {/* close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>

      {loading && !prediction && (
        <div className="flex items-center justify-center p-8">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {prediction && (
        <div className="p-4 flex flex-col gap-3">
          {/* Header */}
          <div className="flex items-start gap-3 pr-6">
            {prediction.analyst_image_url && (
              <img
                src={prediction.analyst_image_url}
                alt={prediction.analyst_name}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-semibold text-sm leading-tight truncate">
                  {prediction.analyst_name}
                </span>
                {prediction.platform && (
                  <span className="text-xs text-gray-400 capitalize">{prediction.platform}</span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{formatDate(prediction.published_at)}</div>
            </div>
          </div>

          {/* Direction badge */}
          {prediction.direction && (
            <div className="flex">
              {prediction.direction === 'bullish' ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                  <TrendingUp size={12} /> Bullish
                </span>
              ) : prediction.direction === 'bearish' ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-medium">
                  <TrendingDown size={12} /> Bearish
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-400 text-xs font-medium">
                  Neutral
                </span>
              )}
            </div>
          )}

          {/* Entry price */}
          {formatPrice(prediction.price_at_prediction) && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Entry Price</div>
              <div className="text-white font-semibold">{formatPrice(prediction.price_at_prediction)}</div>
            </div>
          )}

          {/* Content excerpt */}
          {prediction.content && (
            <div className="rounded-lg bg-[#131722] border border-[#2a2e39] p-3">
              <p className="text-gray-300 text-xs leading-relaxed italic">
                &ldquo;{prediction.content}&rdquo;
              </p>
            </div>
          )}

          {/* Author's Reasoning */}
          {prediction.author_reasoning && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Author's Reasoning</div>
              <p className="text-gray-300 text-xs leading-relaxed">{prediction.author_reasoning}</p>
            </div>
          )}

          {/* AI Insight */}
          {prediction.ai_insight_summary && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">AI Insight</div>
              <p className="text-gray-300 text-xs leading-relaxed">{prediction.ai_insight_summary}</p>
            </div>
          )}

          {/* Footer link */}
          <a
            href={`/analysts/predictions/${prediction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors mt-1"
          >
            View full prediction <ExternalLink size={11} />
          </a>
        </div>
      )}
    </div>
  );
}
